import React, { useEffect, useMemo, useState } from 'react';
import '../../styled/pages/dashboard.scss';
import { Header, Loader, Success, Failed } from '../../components';
import { customStyles } from '../../Utils/utils';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Country, State, City } from 'country-state-city';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { addClinicApi } from '../../rtk-apis/clinics/addClinic';
import { editClinicApi } from '../../rtk-apis/clinics/editClinic';
import { getClinicByIdApi } from '../../rtk-apis/clinics/getClinicById';
import { clearState as clearClinicByIdState } from '../../store/slices/clinics/getClinicById';
import { clearState as clearEditState } from '../../store/slices/clinics/editClinic';
import { clearState } from '../../store/slices/clinics/addClinic';
import { clinicFormValidationSchema } from '../auth/helper/Schema';
import BackButton from '../../components/BackButton';
import BreadCrumbs from '../../components/Breadcrumbs';
import { customList } from 'country-codes-list';

const COUNTRY_CODE_OPTIONS = Array.from(
  new Map(
    Object.entries(customList('countryCode', '{countryCallingCode}'))
      .sort((a, b) => Number(a[1]) - Number(b[1]))
      .map(([code, callingCode]) => [callingCode, code]) // callingCode as key for uniqueness
  ).keys()
).map(callingCode => {
  const padded = callingCode.padStart(2, '0'); // ensures +07 instead of +7
  return (
    <option key={padded} value={`+${padded}`}>
      +{padded}
    </option>
  );
});

// The clinic record is untyped across the app; the form reads it field by field.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ClinicFormProps = { clinicDetails?: any; editMode: boolean };

const ClinicForm = ({ clinicDetails, editMode }: ClinicFormProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [displayMessage, setDisplayMessage] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);

  const [countryCode, setCountryCode] = useState(clinicDetails?.countryCode ?? '+61');
  const [adminCountryCode, setAdminCountryCode] = useState(
    clinicDetails?.adminDetails?.[0]?.countryCode ?? '+61'
  );

  // MO config: fromProviders list (repeatable, editable)
  const [fromProviders, setFromProviders] = useState<Array<{ id: string; name: string }>>(
    () => clinicDetails?.moConfig?.fromProviders?.map((p: { id: string; name?: string }) => ({
      id: p.id || '',
      name: p.name || ''
    })) ?? []
  );

  const handleCountryChange = e => {
    setCountryCode(e.target.value);
  };

  const handleAdminCountryChange = e => {
    setAdminCountryCode(e.target.value);
  };

  // The selects hold ~1,600 options in all. Memoised so a keystroke anywhere on the
  // form does not rebuild them.
  const countryOptionEls = useMemo(
    () =>
      Country.getAllCountries().map(country => (
        <option key={country.isoCode} value={country.isoCode}>
          {country.name}
        </option>
      )),
    []
  );
  const stateOptionEls = useMemo(
    () =>
      stateOptions.map(state => (
        <option key={state.isoCode} value={state.isoCode}>
          {state.name}
        </option>
      )),
    [stateOptions]
  );
  const cityOptionEls = useMemo(
    () =>
      cityOptions.map(city => (
        <option key={city.name} value={city.name}>
          {city.name}
        </option>
      )),
    [cityOptions]
  );

  // ****************** Initail Formik States *********************************
  const contactDetailsWithoutCountryCode =
    clinicDetails?.contactDetails?.replace(clinicDetails?.countryCode, '').trim() ?? '';

  const clinicFormInitialValues = {
    name: clinicDetails?.name ?? '',
    email: clinicDetails?.email ?? '',
    address: clinicDetails?.Address ?? '',
    zipCode: clinicDetails?.ZipCode ?? '',
    country: clinicDetails?.Country ?? '',
    city: clinicDetails?.City ?? '',
    state: clinicDetails?.State ?? '',
    countryCode: clinicDetails?.countryCode ?? '+61',
    contactNumber: contactDetailsWithoutCountryCode,
    // website: clinicDetails?.website ?? '',
    // fax: clinicDetails?.fax ?? '',
    adminName: clinicDetails?.adminDetails?.[0]?.name ?? '',
    adminEmail: clinicDetails?.adminDetails?.[0]?.email ?? '',
    adminContactNumber: clinicDetails?.adminDetails?.[0]?.contactNo ?? '',
    adminCountryCode: clinicDetails?.adminDetails?.[0]?.countryCode ?? '+61',
    moSiteHdPaste: '',
    moSiteHdName: clinicDetails?.moConfig?.siteHd?.name ?? '',
    moSiteHdUuid: clinicDetails?.moConfig?.siteHd?.uuid ?? '',
    moEndpointUrl: clinicDetails?.moConfig?.moEndpointUrl ?? '',
    moInboundCn: clinicDetails?.moConfig?.inboundCN ?? '',
  };

  // ******************* Redux States ************************

  const { status, result, msg, loading } = useSelector(
    (state: RootState) => state.addClinicResponse
  );

  const {
    status: editStatus,
    result: editResult,
    msg: editMsg,
    loading: editLoading,
  } = useSelector((state: RootState) => state.editClinicResponse);

  // ******************************** Use-Effects ***********************************

  useEffect(() => {
    if (selectedCountry) {
      const states = State.getStatesOfCountry(selectedCountry);
      setStateOptions(states);
      setSelectedState('');
      setCityOptions([]);
      setSelectedCity('');
    }
  }, [selectedCountry]);

  // Update cities when state changes
  useEffect(() => {
    if (selectedState) {
      const cities = City.getCitiesOfState(selectedCountry, selectedState);
      setCityOptions(cities);
      setSelectedCity(''); // Reset city selection when state changes
    }
  }, [selectedState, selectedCountry]);

  useEffect(() => {
    if (clinicDetails) {
      setSelectedCountry(clinicDetails?.Country);
      setSelectedState(clinicDetails?.State);
      setSelectedCity(clinicDetails?.City);
    }
  }, [clinicDetails]);

  // Fetch states when country changes
  useEffect(() => {
    if (selectedCountry) {
      const states = State.getStatesOfCountry(selectedCountry);
      setStateOptions(states);
      if (clinicDetails?.State) {
        setSelectedState(clinicDetails?.State); // Prepopulate state
      }
    } else {
      setStateOptions([]);
      setSelectedState('');
    }
  }, [selectedCountry]);

  // Fetch cities when state changes
  useEffect(() => {
    if (selectedState && selectedCountry) {
      const cities = City.getCitiesOfState(selectedCountry, selectedState);
      setCityOptions(cities);
      if (clinicDetails?.City) {
        setSelectedCity(clinicDetails?.City); // Prepopulate city
      }
    } else {
      setCityOptions([]);
      setSelectedCity('');
    }
  }, [selectedState, selectedCountry]);

  const handleSubmit = (values, { setSubmitting }) => {
    const payload = {
      id: editMode ? clinicDetails?._id : undefined,
      name: values.name,
      email: values.email.toLowerCase(),
      countryCode: countryCode,
      contactDetails: values.contactNumber,
      Address: values.address,
      ZipCode: values.zipCode,
      Country: values.country,
      State: values.state,
      City: values.city,
      adminDetails: [
        {
          name: values?.adminName,
          email: values?.adminEmail,
          contactNo: values?.adminContactNumber,
          countryCode: adminCountryCode,
        },
      ],
    };

    const filledProviders = fromProviders.filter(p => p.id.trim() || p.name.trim());
    const hasMoConfig = !!(values.moSiteHdName && values.moSiteHdUuid && values.moEndpointUrl);
    // These used to be dropped without a word, so the providers looked saved but weren't.
    let providerError = '';
    if (filledProviders.some(p => !p.id.trim())) {
      providerError = 'Each sending provider needs a Provider ID.';
    } else if (filledProviders.length > 0 && !hasMoConfig) {
      providerError = 'Sending providers need the MO Site name, Site UUID and Endpoint URL filled in.';
    }
    if (providerError) {
      setDisplayMessage(providerError);
      setIsFailedModalOpen(true);
      setSubmitting(false);
      return;
    }

    if (hasMoConfig) {
      payload.moConfig = {
        siteHd: { name: values.moSiteHdName, uuid: values.moSiteHdUuid, type: 'GUID' },
        moEndpointUrl: values.moEndpointUrl,
        serverType: 'equator',
        // Always sent, so removing the last provider clears the list.
        fromProviders: filledProviders.map(p => ({ id: p.id.trim(), name: p.name.trim(), idType: 'AUSHICPR' })),
        inboundCN: values.moInboundCn?.trim() || undefined,
      };
    }

    if (editMode) {
      dispatch(editClinicApi(payload));
    } else {
      dispatch(addClinicApi(payload));
    }
    setSubmitting(false);
  };

  useEffect(() => {
    const handleSuccess = message => {
      setDisplayMessage(message);
      setIsSuccessModalOpen(true);
      setTimeout(() => {
        setIsSuccessModalOpen(false);
        setDisplayMessage('');
        navigate('/clinics');
      }, 1500);
    };

    const handleFailure = message => {
      setDisplayMessage(message);
      setIsFailedModalOpen(true);
      setTimeout(() => {
        setIsFailedModalOpen(false);
        setDisplayMessage('');
      }, 1500);
    };

    if (status === 'SUCCESS') {
      handleSuccess(msg);
    } else if (editStatus === 'SUCCESS') {
      handleSuccess(editMsg);
    }

    if (status === 'FAILED') {
      handleFailure(msg);
    } else if (editStatus === 'FAILED') {
      handleFailure(editMsg);
    }

    return () => {
      dispatch(clearState());
      dispatch(clearEditState());
    };
  }, [status, editStatus, msg, editMsg, dispatch, navigate]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'BUTTON') {
      e.preventDefault();
    }
  };

  const breadcrumbs = [
    { label: 'Manage Clinics', link: '/clinics', icon: 'sidebar-icon' },
    { label: `${editMode ? 'Edit' : 'Add'} Clinic` },
  ];

  return (
    <>
      <section
        className="layoutContainer"
        style={customStyles.layoutBackground}
      >
        <div className="layoutGrid">
          <div className="page">
            <Header />
            <div className="page__body">
              <Formik
                initialValues={clinicFormInitialValues}
                validationSchema={clinicFormValidationSchema}
                onSubmit={handleSubmit}
                validateOnChange={false}
                enableReinitialize
              >
                {({ isSubmitting, setFieldValue, values }) => (
                  <>
                    <Form className="form" onKeyDown={handleKeyDown}>
                      <div className="page__body__head">
                        <BackButton />
                        <BreadCrumbs breadcrumbs={breadcrumbs} />
                        <div className="btn__group">
                          <button
                            className="btn btn__white btn__outlined"
                            type="button"
                            onClick={() => navigate('/clinics')}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn__primary"
                            type="submit"
                            disabled={isSubmitting}
                          >
                            {editMode ? 'Update' : 'Save'}
                          </button>
                        </div>
                      </div>
                      <section className="form__section">
                        <h1 className="form__section__title">Clinic Details</h1>
                        <div className="formRow formRow__2">
                          <div className="formCol">
                            <div className="form__group">
                              <label
                                htmlFor="name"
                                className="form__label"
                              >
                                Name<span className="required-asterisk">*</span>
                              </label>
                              <Field
                                type="text"
                                id="name"
                                name="name"
                                className="form__input form__input--sm"
                                placeholder="Name"
                              />
                              <ErrorMessage
                                name="name"
                                component="div"
                                className="form__error"
                              />
                            </div>
                          </div>

                          <div className="formCol">
                            <div className="form__group">
                              <label
                                htmlFor="email"
                                className="form__label"
                              >
                                Email Address<span className="required-asterisk">*</span>
                              </label>
                              <Field
                                type="text"
                                id="email"
                                name="email"
                                className="form__input form__input--sm"
                                placeholder="Email Address"
                                // disabled={editMode}
                              />
                              <ErrorMessage
                                name="email"
                                component="div"
                                className="form__error"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="formRow formRow__2">
                          <div className="formCol">
                            <div className="form__group">
                              <label
                                htmlFor="address"
                                className="form__label"
                              >
                                Address<span className="required-asterisk">*</span>
                              </label>
                              <Field
                                type="text"
                                id="address"
                                name="address"
                                className="form__input form__input--sm"
                                placeholder="Address"
                              />
                              <ErrorMessage
                                name="address"
                                component="div"
                                className="form__error"
                              />
                            </div>
                          </div>

                          <div className="formCol">
                            <div className="formRow formRow__4">
                              <div className="formCol">
                                <div className="form__group">
                                  <label
                                    htmlFor="zipCode"
                                    className="form__label"
                                  >
                                    Zip Code<span className="required-asterisk">*</span>
                                  </label>
                                  <Field
                                    type="text"
                                    id="zipCode"
                                    name="zipCode"
                                    className="form__input form__input--sm"
                                    placeholder="Zip Code"
                                  />
                                  <ErrorMessage
                                    name="zipCode"
                                    component="div"
                                    className="form__error"
                                  />
                                </div>
                              </div>

                              <div className="formCol">
                                <div className="form__group">
                                  <label
                                    htmlFor="country"
                                    className="form__label"
                                  >
                                    Country<span className="required-asterisk">*</span>
                                  </label>
                                  <Field
                                    as="select"
                                    id="country"
                                    name="country"
                                    value={selectedCountry}
                                    onChange={e => {
                                      setSelectedCountry(e.target.value);
                                      setFieldValue('country', e.target.value);
                                      setFieldValue('state', '');
                                      setFieldValue('city', '');
                                      setSelectedState('');
                                      setSelectedCity('');
                                    }}
                                    className="form__select form__select--sm"
                                    style={customStyles.select}
                                  >
                                    <option value="">-Select-</option>
                                    {countryOptionEls}
                                  </Field>
                                  <ErrorMessage
                                    name="country"
                                    component="div"
                                    className="form__error"
                                  />
                                </div>
                              </div>

                              <div className="formCol">
                                <div className="form__group">
                                  <label
                                    htmlFor="State"
                                    className="form__label"
                                  >
                                    State<span className="required-asterisk">*</span>
                                  </label>
                                  <Field
                                    as="select"
                                    id="state"
                                    name="state"
                                    value={selectedState}
                                    onChange={e => {
                                      setSelectedState(e.target.value);
                                      setFieldValue('state', e.target.value);
                                      setFieldValue('city', '');
                                      setSelectedCity('');
                                    }}
                                    className="form__select form__select--sm"
                                    style={customStyles.select}
                                    disabled={!selectedCountry}
                                  >
                                    <option value="">-Select-</option>
                                    {stateOptionEls}
                                  </Field>
                                  <ErrorMessage
                                    name="city"
                                    component="div"
                                    className="form__error"
                                  />
                                </div>
                              </div>

                              <div className="formCol">
                                <div className="form__group">
                                  <label
                                    htmlFor="city"
                                    className="form__label"
                                  >
                                    City<span className="required-asterisk">*</span>
                                  </label>
                                  <Field
                                    as="select"
                                    id="city"
                                    name="city"
                                    value={selectedCity}
                                    onChange={e => {
                                      setSelectedCity(e.target.value);
                                      setFieldValue('city', e.target.value);
                                    }}
                                    className="form__select form__select--sm"
                                    style={customStyles.select}
                                    disabled={!selectedState}
                                  >
                                    <option value="">-Select-</option>
                                    {cityOptionEls}
                                  </Field>
                                  <ErrorMessage
                                    name="state"
                                    component="div"
                                    className="form__error"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="formRow formRow__2">
                          <div className="formCol">
                            <div className="form__group">
                              <label
                                htmlFor="contactNumber"
                                className="form__label"
                              >
                                Contact Number<span className="required-asterisk">*</span>
                              </label>
                              <div className="form__groupField form__groupField--phonenumber">
                                <Field
                                  as="select"
                                  name="countryCode"
                                  className="form__select form__select--sm"
                                  style={customStyles.contactSelect}
                                  id="countryCode"
                                  defaultValue="+61"
                                  value={countryCode}
                                  onChange={handleCountryChange}
                                >
                                  {COUNTRY_CODE_OPTIONS}
                                </Field>
                                <Field
                                  type="tel"
                                  name="contactNumber"
                                  className="form__input form__input--sm"
                                  placeholder="Contact Number"
                                  maxLength={10}
                                  onInput={e => (e.target.value = e.target.value.replace(/\D/, ''))}
                                />
                              </div>
                              <ErrorMessage
                                name="contactNumber"
                                component="div"
                                className="form__error"
                              />
                            </div>
                          </div>
                          {/* <div className="formCol">
                            <div className="form__group">
                              <label
                                htmlFor="website"
                                className="form__label"
                              >
                                Website
                              </label>
                              <Field
                                type="text"
                                id="website"
                                name="website"
                                className="form__input form__input--sm"
                                placeholder="Website"
                              />
                            </div>
                          </div> */}
                        </div>

                        {/* <div className="formRow formRow__2">
                          <div className="formCol">
                            <div className="form__group">
                              <label
                                htmlFor="fax"
                                className="form__label"
                              >
                                Fax
                              </label>
                              <Field
                                type="text"
                                id="fax"
                                name="fax"
                                className="form__input form__input--sm"
                                placeholder="Fax"
                              />
                            </div>
                          </div>
                        </div> */}

                        {/* ADMIN DEATILS */}
                        <div className="form__section__divider"></div>
                        <h1 className="form__section__title">Admin Details</h1>
                        <div className="formRow formRow__2">
                          <div className="formCol">
                            <div className="form__group">
                              <label
                                htmlFor="name"
                                className="form__label"
                              >
                                Name<span className="required-asterisk">*</span>
                              </label>
                              <Field
                                type="text"
                                id="adminName"
                                name="adminName"
                                className="form__input form__input--sm"
                                placeholder="Name"
                              />
                              <ErrorMessage
                                name="adminName"
                                component="div"
                                className="form__error"
                              />
                            </div>
                          </div>

                          <div className="formCol">
                            <div className="form__group">
                              <label
                                htmlFor="email"
                                className="form__label"
                              >
                                Email Address<span className="required-asterisk">*</span>
                              </label>
                              <Field
                                type="text"
                                id="adminEmail"
                                name="adminEmail"
                                className="form__input form__input--sm"
                                placeholder="Email Address"
                                disabled={editMode}
                              />
                              <ErrorMessage
                                name="adminEmail"
                                component="div"
                                className="form__error"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="formRow formRow__2">
                          <div className="formCol">
                            <div className="form__group">
                              <label
                                htmlFor="footerContactNumber"
                                className="form__label"
                              >
                                Contact Number<span className="required-asterisk">*</span>
                              </label>
                              <div className="form__groupField form__groupField--phonenumber">
                                <Field
                                  as="select"
                                  name="adminCountryCode"
                                  className="form__select form__select--sm"
                                  style={customStyles.contactSelect}
                                  defaultValue="+61"
                                  value={adminCountryCode}
                                  onChange={handleAdminCountryChange}
                                >
                                  {COUNTRY_CODE_OPTIONS}
                                </Field>
                                <Field
                                  type="text"
                                  id="adminContactNumber"
                                  name="adminContactNumber"
                                  className="form__input form__input--sm"
                                  placeholder="Contact Number"
                                  maxLength={10}
                                />
                              </div>
                              <ErrorMessage
                                    name="adminContactNumber"
                                    component="div"
                                    className="form__error"
                                  />
                            </div>
                          </div>
                        </div>

                        {/* Medical Objects (MO) Configuration */}
                        <div className="form__section__divider"></div>
                        <h1 className="form__section__title">Medical Objects (MO) Configuration</h1>
                        <p className="form__help" style={{ marginBottom: '1rem', color: '#94a3b8' }}>
                          Optional. Configure when MO provides Site HD and URL for this clinic.
                        </p>
                        <div className="formRow formRow__2">
                          <div className="formCol">
                            <div className="form__group">
                              <label htmlFor="moSiteHdPaste" className="form__label">
                                Site HD (paste from MO email)
                              </label>
                              <Field name="moSiteHdPaste">
                                {({ field }) => (
                                  <input
                                    {...field}
                                    id="moSiteHdPaste"
                                    type="text"
                                    className="form__input form__input--sm"
                                    placeholder="Name^UUID^GUID"
                                    onBlur={e => {
                                      const val = e.target.value.trim();
                                      if (val && val.includes('^')) {
                                        const parts = val.split('^');
                                        if (parts.length >= 2) {
                                          setFieldValue('moSiteHdName', parts[0]?.trim() || '');
                                          setFieldValue('moSiteHdUuid', parts[1]?.trim() || '');
                                        }
                                      }
                                    }}
                                  />
                                )}
                              </Field>
                            </div>
                          </div>
                        </div>
                        <div className="formRow formRow__2">
                          <div className="formCol">
                            <div className="form__group">
                              <label htmlFor="moSiteHdName" className="form__label">Site Name</label>
                              <Field
                                type="text"
                                id="moSiteHdName"
                                name="moSiteHdName"
                                className="form__input form__input--sm"
                                placeholder="e.g. Yarrabilba Cardiology"
                              />
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__group">
                              <label htmlFor="moSiteHdUuid" className="form__label">Site UUID</label>
                              <Field
                                type="text"
                                id="moSiteHdUuid"
                                name="moSiteHdUuid"
                                className="form__input form__input--sm"
                                placeholder="e.g. EB71B611-EDDC-4561-B411-B2E354307F27"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="formRow formRow__2">
                          <div className="formCol">
                            <div className="form__group">
                              <label htmlFor="moEndpointUrl" className="form__label">MO Endpoint URL</label>
                              <Field
                                type="text"
                                id="moEndpointUrl"
                                name="moEndpointUrl"
                                className="form__input form__input--sm"
                                placeholder="https://hd-xxx.mocloud.com.au"
                              />
                              <ErrorMessage name="moEndpointUrl" component="div" className="form__error" />
                            </div>
                          </div>
                        </div>
                        <div className="formRow formRow__2">
                          <div className="formCol">
                            <div className="form__group">
                              <label htmlFor="moInboundCn" className="form__label">ACK Client Certificate Common Name</label>
                              <Field
                                type="text"
                                id="moInboundCn"
                                name="moInboundCn"
                                className="form__input form__input--sm"
                                placeholder="e.g. hd.e7b72116-d177-4c33-97c2-974caff5376e-guid.id.medical-objects.com.au"
                              />
                              <p className="form__help" style={{ marginTop: '0.25rem', color: '#94a3b8', fontSize: '0.875rem' }}>
                                From MO email: &quot;Return Ack Client Certificate Common Name&quot;
                              </p>
                              <ErrorMessage name="moInboundCn" component="div" className="form__error" />
                            </div>
                          </div>
                        </div>
                        <div className="form__group">
                          <label className="form__label">Registered Sending Providers</label>
                          <p className="form__help" style={{ marginBottom: '0.5rem', color: '#94a3b8', fontSize: '0.875rem' }}>
                            Doctors MO registered for this clinic (Provider ID + Name)
                          </p>
                          {fromProviders.map((p, idx) => (
                            <div key={idx} className="formRow formRow__2" style={{ marginBottom: '0.5rem', alignItems: 'flex-end' }}>
                              <div className="formCol">
                                <input
                                  type="text"
                                  className="form__input form__input--sm"
                                  placeholder="Provider ID"
                                  value={p.id}
                                  onChange={e => {
                                    const next = [...fromProviders];
                                    next[idx] = { ...next[idx], id: e.target.value };
                                    setFromProviders(next);
                                  }}
                                />
                              </div>
                              <div className="formCol">
                                <input
                                  type="text"
                                  className="form__input form__input--sm"
                                  placeholder="Name"
                                  value={p.name}
                                  onChange={e => {
                                    const next = [...fromProviders];
                                    next[idx] = { ...next[idx], name: e.target.value };
                                    setFromProviders(next);
                                  }}
                                />
                              </div>
                              <div className="formCol" style={{ flex: '0 0 auto' }}>
                                <button
                                  type="button"
                                  className="btn btn__white btn__outlined"
                                  onClick={() => setFromProviders(fromProviders.filter((_, i) => i !== idx))}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="btn btn__white btn__outlined"
                            onClick={() => setFromProviders([...fromProviders, { id: '', name: '' }])}
                          >
                            + Add Provider
                          </button>
                        </div>
                      </section>
                    </Form>
                  </>
                )}
              </Formik>
            </div>
          </div>
        </div>
        <Loader isLoading={loading} />
      </section>
      <Success
        isSuccessModalOpen={isSuccessModalOpen}
        handleCloseModal={() => {
          setIsSuccessModalOpen(false);
        }}
        title={displayMessage}
      />
      <Failed
        isFailedModalOpen={isFailedModalOpen}
        handleCloseModal={() => {
          setIsFailedModalOpen(false);
        }}
        title={displayMessage}
      />
    </>
  );
};

// Edit mode loads the clinic by the URL id, so a pasted link, new tab or refresh
// gets the saved record instead of a blank form.
const AddUpdateClinic = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { id } = useParams();
  const editMode = location.pathname.includes('updateclinic');
  const { result, status, msg } = useSelector((state: RootState) => state.getClinicByIdResponse);

  useEffect(() => {
    if (editMode && id) {
      dispatch(getClinicByIdApi(id));
    }
    return () => {
      dispatch(clearClinicByIdState());
    };
  }, [editMode, id, dispatch]);

  if (!editMode) {
    return <ClinicForm editMode={false} />;
  }

  const clinic = result as { _id?: string } | null;
  if (status === 'SUCCESS' && clinic?._id === id) {
    return (
      <ClinicForm
        key={clinic._id}
        clinicDetails={clinic}
        editMode
      />
    );
  }

  return (
    <section
      className="layoutContainer"
      style={customStyles.layoutBackground}
    >
      <div className="layoutGrid">
        <div className="page">
          <Header />
          <div className="page__body">
            {status === 'ERROR' ? (
              <div className="page__body__head">
                <BackButton />
                <span>{msg || 'Could not load this clinic.'}</span>
              </div>
            ) : (
              <Loader isLoading />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AddUpdateClinic;
